import React from 'react'
import styled from 'styled-components'
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'core-system/Modal'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import SurveyIcon from 'core-system/Icons/Concierge/Survey'
import { ElectricBike } from 'core-system/Icons/modeTypes'
import { Helmet, Location } from 'core-system/Icons/Misc'
import { Locations } from 'shared/Router/Locations'

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`

const Subtitle = styled(Text)`
  margin-top: -0.25rem;
`

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const Item = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
`

const IconWrap = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.625rem;
  background-color: ${palette.secondary.purple100};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const ItemCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`

const HelpLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: ${palette.secondary.purple700};
  font-weight: 600;
  text-decoration: none;
  width: fit-content;

  &:hover {
    text-decoration: underline;
  }
`

type PilotWelcomeItem = {
  title: string
  description: string
  icon: React.ReactNode
  helpLink?: {
    label: string
    href: string
  }
}

const PILOT_WELCOME_ITEMS: PilotWelcomeItem[] = [
  {
    title: 'Wear a helmet',
    description:
      "Helmets are required whenever you ride. We'll ask whether you have one or need one.",
    icon: (
      <Helmet width={20} height={20} color={palette.secondary.purple700} />
    ),
  },
  {
    title: 'Complete the surveys',
    description:
      "You'll get a short survey at the start and end of the pilot from the UW research team.",
    icon: (
      <SurveyIcon width={20} height={20} color={palette.secondary.purple700} />
    ),
  },
  {
    title: 'Ride at least 10 miles a month',
    description:
      'The program asks participants to ride a minimum of 10 miles each month.',
    icon: (
      <ElectricBike
        width={20}
        height={20}
        color={palette.secondary.purple700}
      />
    ),
  },
  {
    title: 'Log your trips',
    description:
      'Track rides in the Fleet app so we can measure program impact.',
    icon: (
      <Location width={20} height={20} color={palette.secondary.purple700} />
    ),
    helpLink: {
      label: 'How to log a trip',
      href: Locations.EmployeeTrips.Overview,
    },
  },
]

interface KingCountyPilotWelcomeModalProps {
  open: boolean
  onClose: () => void
}

const KingCountyPilotWelcomeModal = React.memo(
  (props: KingCountyPilotWelcomeModalProps) => {
    const { open, onClose } = props

    return (
      <Modal open={open} onClose={onClose} width='32rem'>
        <ModalHeader title='Welcome to the King County e-bike pilot' />
        <ModalBody>
          <Content>
            <Subtitle variant='body2' textColor={palette.text.secondary}>
              A few things to know before you start
            </Subtitle>
            <ItemList>
              {PILOT_WELCOME_ITEMS.map((item) => (
                <Item key={item.title}>
                  <IconWrap>{item.icon}</IconWrap>
                  <ItemCopy>
                    <Text variant='action2'>{item.title}</Text>
                    <Text variant='body2' textColor={palette.text.secondary}>
                      {item.description}
                    </Text>
                    {item.helpLink ? (
                      <HelpLink href={item.helpLink.href}>
                        {item.helpLink.label}
                        <span aria-hidden='true'>↗</span>
                      </HelpLink>
                    ) : null}
                  </ItemCopy>
                </Item>
              ))}
            </ItemList>
          </Content>
        </ModalBody>
        <ModalFooter>
          <PrimaryButton fullWidth onClick={onClose}>
            Got it — let&apos;s go
          </PrimaryButton>
        </ModalFooter>
      </Modal>
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  KingCountyPilotWelcomeModal.displayName = 'KingCountyPilotWelcomeModal'
}

export default KingCountyPilotWelcomeModal
